import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { CreateIpAccrual, UpdateIpAccrual } from '@repo/backend/crt'
import { IP_ACCRUALS_CREATE_ERROR_TYPES, PRODUCT_CATEGORIES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { monthStartString } from '@repo/zod-schemas'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const ipAccrualsSearchSchema = z.object({
  keyword: z.string().optional(),
  period: monthStartString.optional(),
  ...datatableSearchSchema.shape,
})

export const ipAccrualsDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  budgetAllocation: z.array(z.string()).optional(),
  conditionType: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  hierarchyErp: z.array(z.string()).optional(),
  masterAgreement: z.array(z.string()).optional(),
  agreementNumber: z.array(z.string()).optional(),
  investmentType: z.array(z.string()).optional(),
  billingDateFrom: z.coerce.date().optional(),
  billingDateTo: z.coerce.date().optional(),
  ...datatableSearchSchema.shape,
})

export type IpAccrualsSearch = z.infer<typeof ipAccrualsSearchSchema>
export type IpAccrualsDetailsSearch = z.infer<typeof ipAccrualsDetailsSearchSchema>

export type IpAccrualsList = Awaited<ReturnType<typeof getIpAccrualsList>>
export type IpAccrualListItem = IpAccrualsList['results'][number]
export type IpAccrualWithDetails = Awaited<ReturnType<typeof getIpAccrualById>>

type UseCreateIpAccrualArgs = {
  queries: IpAccrualsSearch
  handleOnSuccess: (ipAccrualId: number) => void
}

type UseUpdateIpAccrualArgs = QueryHandlers & {
  ipAccrualId: number
}

type UseDeleteIpAccrualsArgs = QueryHandlers & {
  ipAccrualIds: number[]
  queries: IpAccrualsSearch
}

async function getCurrentIpAccrual(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const query = crtPeriod ? { crtPeriod } : {}
  const res = await api.crt['ip-accruals'].current.$get({ query }, { init: { signal } })
  return await res.json()
}

export const currentIpAccrualQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentIpAccrual', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getCurrentIpAccrual(crtPeriod, signal),
})

export function useCurrentIpAccrual(crtPeriod?: string) {
  return useQuery(currentIpAccrualQueryOptions(crtPeriod))
}

/**
 * The import holding one period — its month of billing — or `null`. Detail pages are addressed by that
 * period, so their loader resolves the import's id from it through the list endpoint's period filter.
 */
export const ipAccrualByPeriodQueryOptions = (period: string) => ({
  queryKey: ['ipAccrualByPeriod', period] as const,
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const { results } = await getIpAccrualsList({ period, page: 1, pageSize: 1 }, signal)
    return results[0] ?? null
  },
})

async function getLatestIpAccrual(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['ip-accruals'].latest.$get({}, { init: { signal } })
  return await res.json()
}

export function useLatestIpAccrual() {
  return useQuery({
    queryKey: ['latestIpAccrual'] as const,
    queryFn: ({ signal }) => getLatestIpAccrual(signal),
  })
}

async function getIpAccrualsList(search: IpAccrualsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['ip-accruals'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function getIpAccrualById(
  id: string,
  queries: IpAccrualsDetailsSearch = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const { billingDateFrom, billingDateTo, ...rest } = queries
  const res = await api.crt['ip-accruals'][':id'].$get(
    {
      param: { id },
      query: stringifyValues({
        ...rest,
        billingDateFrom: billingDateFrom ? formatDateOnly(billingDateFrom) : undefined,
        billingDateTo: billingDateTo ? formatDateOnly(billingDateTo) : undefined,
      }),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function createIpAccrual(payload: CreateIpAccrual) {
  const api = getApi()

  const res = await api.crt['ip-accruals'].$post({
    json: payload,
  })

  return await res.json()
}

async function updateIpAccrual(ipAccrualId: number, details: UpdateIpAccrual) {
  const api = getApi()

  await api.crt['ip-accruals'][':id'].$patch({
    param: { id: String(ipAccrualId) },
    json: details,
  })
}

async function deleteIpAccruals(ipAccrualIds: number[]) {
  const api = getApi()

  await api.crt['ip-accruals'].bulk.$delete({
    json: { ids: ipAccrualIds },
  })
}

export function useIpAccrualsList(queries: IpAccrualsSearch) {
  return useQuery({
    queryKey: ['ipAccruals', queries],
    queryFn: ({ signal }) => getIpAccrualsList(queries, signal),
  })
}

export const ipAccrualByIdQueryOptions = (id: string, queries: IpAccrualsDetailsSearch = {}) => ({
  queryKey: ['ipAccrualById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getIpAccrualById(id, queries, signal),
})

export function useIpAccrualById(id: string, queries: IpAccrualsDetailsSearch = {}) {
  return useQuery(ipAccrualByIdQueryOptions(id, queries))
}

export type CreatedIpAccrualExportJob = Awaited<ReturnType<typeof createIpAccrualDetailsExportJob>>

type UseCreateIpAccrualExportJobArgs = {
  ipAccrualId: string
  onJobCreated: (job: CreatedIpAccrualExportJob) => void
}

async function createIpAccrualDetailsExportJob(id: string, queries: IpAccrualsDetailsSearch = {}) {
  const api = getApi()
  const { billingDateFrom, billingDateTo, ...rest } = queries
  const res = await api.crt['ip-accruals'][':id']['export-jobs'].$post({
    param: { id },
    query: stringifyValues({
      ...rest,
      billingDateFrom: billingDateFrom ? formatDateOnly(billingDateFrom) : undefined,
      billingDateTo: billingDateTo ? formatDateOnly(billingDateTo) : undefined,
    }),
  })

  return await res.json()
}

async function createIpAccrualRawExportJob(id: string) {
  const api = getApi()
  const res = await api.crt['ip-accruals'][':id']['raw-export-jobs'].$post({ param: { id } })
  return await res.json()
}

export function useCreateIpAccrualDetailsExportJob({
  ipAccrualId,
  onJobCreated,
}: UseCreateIpAccrualExportJobArgs) {
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createIpAccrualDetailsExportJob', ipAccrualId],
    mutationFn: (queries: IpAccrualsDetailsSearch) =>
      createIpAccrualDetailsExportJob(ipAccrualId, queries),
    onSuccess: (job) => {
      onJobCreated(job)
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('export.startFailed', { ns: 'common' }),
        className: toastClassName,
      })
    },
  })
}

export function useCreateIpAccrualRawExportJob({
  ipAccrualId,
  onJobCreated,
}: UseCreateIpAccrualExportJobArgs) {
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createIpAccrualRawExportJob', ipAccrualId],
    mutationFn: () => createIpAccrualRawExportJob(ipAccrualId),
    onSuccess: (job) => {
      onJobCreated(job)
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('export.startFailed', { ns: 'common' }),
        className: toastClassName,
      })
    },
  })
}

export function useCreateIpAccrual({ queries, handleOnSuccess }: UseCreateIpAccrualArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['ipAccruals', queries],
    mutationFn: createIpAccrual,
    onSuccess: (ipAccrual) => {
      handleOnSuccess(ipAccrual.id)

      queryClient.invalidateQueries({
        queryKey: ['ipAccruals', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('ipAccruals.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; period?: string }
        | undefined

      let description: string
      switch (customParams?.type) {
        case IP_ACCRUALS_CREATE_ERROR_TYPES.periodConflict:
          description = `${t('errors.ipAccruals.createFailed', { ns: 'crt' })}: ${t(
            'errors.ipAccruals.periodConflict',
            { ns: 'crt', period: customParams.period },
          )}`
          break
        default:
          description = getFirstZodErrorMessage(issues, t, 'errors.ipAccruals.createFailed', 'crt')
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateIpAccrual({ ipAccrualId, handleOnSuccess }: UseUpdateIpAccrualArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateIpAccrual', ipAccrualId],
    mutationFn: (details: UpdateIpAccrual) => updateIpAccrual(ipAccrualId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['ipAccrualById', String(ipAccrualId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('ipAccruals.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.ipAccruals.updateFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteIpAccruals({
  ipAccrualIds,
  queries,
  handleOnSuccess,
}: UseDeleteIpAccrualsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteIpAccruals'],
    mutationFn: () => deleteIpAccruals(ipAccrualIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['ipAccruals', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('ipAccruals.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.ipAccruals.deleteFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
